/**
 * Shared snapshot comparison display components.
 * Extracted so both SnapshotComparisonToolPage and RollbackInitiationPage can
 * render the same rich diff UI without duplicating code.
 *
 * Exports:
 *  - PropertyChangeNode  — recursive property-level diff renderer
 *  - StatusBadge         — Added / Modified / Removed badge
 *  - ResourceTypeRow     — accordion row for one resource type group
 *  - SnapshotComparisonView (default) — top-level accordion of all changed groups
 */

import React from 'react';
import { ui } from '../../config/uiSizes';
import type {
    SnapshotResourceTypeGroup,
    ComparisonPropertyChange,
    ChangeStatus,
} from '../../types/snapshotComparison';
import {
    formatValue,
    camelCaseToTitleCase,
    formatPropertyName,
    extractResourceName,
    toFriendlyName,
} from '../../utils/formatters';
import { getTypeLabelByApiName } from '../../utils/resourceTypes';

// ── Helpers ────────────────────────────────────────────────────────────────

/** Normalises Modified→Added/Deleted when one side is empty/null. */
const getActualChangeType = (child: ComparisonPropertyChange): ChangeStatus => {
    if (
        child.changeType === 'Modified' &&
        (child.afterValue === null || child.afterValue === undefined || child.afterValue === '')
    ) {
        return 'Deleted';
    }
    if (
        child.changeType === 'Modified' &&
        (child.beforeValue === null || child.beforeValue === undefined || child.beforeValue === '')
    ) {
        return 'Added';
    }
    return child.changeType;
};

// ── PropertyChangeNode ─────────────────────────────────────────────────────

export const PropertyChangeNode: React.FC<{
    node: ComparisonPropertyChange;
    depth?: number;
    index?: number;
}> = ({ node, depth = 0, index }) => {
    const hasChildren = node.children && node.children.length > 0;
    const isLeaf = !hasChildren;
    const isPermissionPolicy = node.propertyName === 'permission_policies';

    // ── Case 1: permission_policy leaf ────────────────────────────────────────
    if (isPermissionPolicy && isLeaf && (node.changeType === 'Added' || node.changeType === 'Deleted')) {
        const value = node.changeType === 'Added' ? node.afterValue : node.beforeValue;
        const policyData = value as Record<string, unknown> | null;

        const domain = (policyData?.['domain'] as string) || '';
        const entityName =
            (policyData?.['entityName'] as string) ||
            (policyData?.['entity_name'] as string) ||
            (policyData?.['name'] as string) ||
            '';
        const actionSet = policyData?.['action_set'] as string[] | undefined;
        const hasAllPermissions = actionSet?.includes('*');

        const displayDomain = camelCaseToTitleCase(domain);
        const displayEntity = camelCaseToTitleCase(entityName);
        const displayAction = hasAllPermissions ? 'All Permissions' : (actionSet?.join(', ') || '—');
        const breadcrumb = `${displayDomain} > ${displayEntity} > ${displayAction}`;

        const isEvenRow = index !== undefined && index % 2 === 0;
        const rowBgColor =
            node.changeType === 'Added'
                ? isEvenRow ? 'bg-green-50/40 dark:bg-green-950/15' : 'bg-green-100/80 dark:bg-green-900/25'
                : isEvenRow ? 'bg-red-50/40 dark:bg-red-950/15' : 'bg-red-100/80 dark:bg-red-900/25';
        const hoverColor =
            node.changeType === 'Added'
                ? 'hover:bg-green-200 dark:hover:bg-green-900/35'
                : 'hover:bg-red-100 dark:hover:bg-red-900/35';

        return (
            <div
                className={`px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 ${hoverColor} transition-colors cursor-default ${rowBgColor}`}
            >
                <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 select-text`} title={breadcrumb}>
                    {breadcrumb}
                </span>
            </div>
        );
    }

    // ── Case 2: regular leaf node ─────────────────────────────────────────────
    if (isLeaf) {
        const before = formatValue(node.beforeValue);
        const after = formatValue(node.afterValue);

        let beforeDisplay = extractResourceName(before);
        let afterDisplay = extractResourceName(after);

        if (
            typeof node.beforeValue === 'string' &&
            node.beforeValue.includes('_') &&
            beforeDisplay === before &&
            !before.includes('${') &&
            !before.includes('.id')
        ) {
            beforeDisplay = formatPropertyName(node.beforeValue);
        }
        if (
            typeof node.afterValue === 'string' &&
            node.afterValue.includes('_') &&
            afterDisplay === after &&
            !after.includes('${') &&
            !after.includes('.id')
        ) {
            afterDisplay = formatPropertyName(node.afterValue);
        }

        // Guard against objects serialized as "[object Object]" by the backend
        if (beforeDisplay === '[object Object]') {
            if (typeof node.beforeValue === 'object' && node.beforeValue !== null) {
                const pairs = Object.entries(node.beforeValue as Record<string, unknown>)
                    .filter(([, v]) => v !== null && v !== undefined && String(v) !== '[object Object]')
                    .map(([k, v]) => `${formatPropertyName(k)}: ${String(v)}`);
                beforeDisplay = pairs.length > 0 ? pairs.join(' · ') : '—';
            } else {
                beforeDisplay = '—';
            }
        }
        if (afterDisplay === '[object Object]') {
            if (typeof node.afterValue === 'object' && node.afterValue !== null) {
                const pairs = Object.entries(node.afterValue as Record<string, unknown>)
                    .filter(([, v]) => v !== null && v !== undefined && String(v) !== '[object Object]')
                    .map(([k, v]) => `${formatPropertyName(k)}: ${String(v)}`);
                afterDisplay = pairs.length > 0 ? pairs.join(' · ') : '—';
            } else {
                afterDisplay = '—';
            }
        }

        const isEvenRow = index !== undefined && index % 2 === 0;
        let actualChangeType: ChangeStatus = node.changeType;
        if (
            node.changeType === 'Modified' &&
            (node.afterValue === null || node.afterValue === undefined || node.afterValue === '')
        ) {
            actualChangeType = 'Deleted';
        } else if (
            node.changeType === 'Modified' &&
            (node.beforeValue === null || node.beforeValue === undefined || node.beforeValue === '')
        ) {
            actualChangeType = 'Added';
        }

        if (actualChangeType === 'Added' || actualChangeType === 'Deleted') {
            const displayValue = actualChangeType === 'Added' ? afterDisplay : beforeDisplay;
            const rowBgColor =
                actualChangeType === 'Added'
                    ? isEvenRow ? 'bg-green-50/40 dark:bg-green-950/15' : 'bg-green-100/80 dark:bg-green-900/25'
                    : isEvenRow ? 'bg-red-50/40 dark:bg-red-950/15' : 'bg-red-100/80 dark:bg-red-900/25';
            const hoverColor =
                actualChangeType === 'Added'
                    ? 'hover:bg-green-200 dark:hover:bg-green-900/35'
                    : 'hover:bg-red-100 dark:hover:bg-red-900/35';
            const formattedPropertyName = formatPropertyName(node.propertyName);

            return (
                <div
                    className={`grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 ${hoverColor} transition-colors cursor-default items-center ${rowBgColor}`}
                >
                    <span
                        className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 font-medium truncate select-text`}
                        title={formattedPropertyName}
                    >
                        {formattedPropertyName}
                    </span>
                    <span
                        className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 truncate select-text`}
                        title={displayValue}
                    >
                        {displayValue}
                    </span>
                </div>
            );
        }

        // Modified: 3-column
        const rowBgColor = isEvenRow
            ? 'bg-orange-50/40 dark:bg-orange-950/15'
            : 'bg-orange-100/90 dark:bg-orange-900/30';
        const hoverColor = 'hover:bg-orange-200 dark:hover:bg-orange-900/35';
        const formattedPropertyName = formatPropertyName(node.propertyName);

        return (
            <div
                className={`grid grid-cols-[1fr_1.5fr_1.5fr] gap-2 lg:gap-3 px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 ${hoverColor} transition-colors cursor-default items-center ${rowBgColor}`}
            >
                <span
                    className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 font-medium truncate select-text`}
                    title={formattedPropertyName}
                >
                    {formattedPropertyName}
                </span>
                <span
                    className={`${ui.text.xs} lg:text-sm text-red-600 dark:text-red-400 truncate select-text`}
                    title={before}
                >
                    {beforeDisplay}
                </span>
                <span
                    className={`${ui.text.xs} lg:text-sm text-green-600 dark:text-green-400 truncate select-text`}
                    title={after}
                >
                    {afterDisplay}
                </span>
            </div>
        );
    }

    // ── Case 3: depth 0, permission_policies branch — multi-column layout ─────
    if (depth === 0 && node.propertyName === 'permission_policies' && hasChildren && node.children) {
        const addedCount = node.children.filter((c) => c.changeType === 'Added').length;
        const deletedCount = node.children.filter((c) => c.changeType === 'Deleted').length;
        const totalChanges = node.children.length;

        const borderColor =
            node.changeType === 'Added'
                ? 'border-green-300 dark:border-green-700'
                : node.changeType === 'Deleted'
                    ? 'border-red-300 dark:border-red-700'
                    : 'border-orange-300 dark:border-orange-700';

        const textColor =
            node.changeType === 'Added'
                ? 'text-green-700 dark:text-green-300'
                : node.changeType === 'Deleted'
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-orange-700 dark:text-orange-300';

        return (
            <div className="pl-8 lg:pl-10 pr-4 py-3 lg:py-4 cursor-default">
                <div className={`border-l-4 ${borderColor} pl-4 mb-4 cursor-default select-none`}>
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className={`${ui.text.sectionTitle} font-bold ${textColor} select-text`}>
                            {formatPropertyName(node.propertyName)}
                        </span>
                        <StatusBadge status={node.changeType} />
                        <span
                            className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full ${node.changeType === 'Added'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : node.changeType === 'Deleted'
                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                    : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                                }`}
                        >
                            {totalChanges} {totalChanges === 1 ? 'change' : 'changes'}
                        </span>
                    </div>
                </div>

                <div
                    className={`grid ${addedCount > 0 && deletedCount > 0 ? ui.widthAndHeight.comparisonGrid : 'grid-cols-1'
                        } gap-4 lg:gap-6`}
                >
                    {addedCount > 0 && (
                        <div className="border-2 border-green-200 dark:border-green-800/60 rounded-lg overflow-hidden shadow-sm">
                            <div className="bg-green-100 dark:bg-green-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-green-200 dark:border-green-800/60 cursor-default select-none">
                                <h4 className={`${ui.text.sm} font-bold text-green-700 dark:text-green-300 uppercase tracking-wider`}>
                                    Added ({addedCount})
                                </h4>
                            </div>
                            <div className="px-2 lg:px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800/40 cursor-default select-none">
                                <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>
                                    Domain {'>'} Entity Name {'>'} Action
                                </span>
                            </div>
                            <div
                                className={`${ui.widthAndHeight.comparisonColumnHeight} overflow-y-auto bg-green-50/30 dark:bg-green-950/10 cursor-default`}
                            >
                                {node.children
                                    .filter((c) => c.changeType === 'Added')
                                    .map((child, idx) => (
                                        <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                                    ))}
                            </div>
                        </div>
                    )}

                    {deletedCount > 0 && (
                        <div className="border-2 border-red-200 dark:border-red-800/60 rounded-lg overflow-hidden shadow-sm">
                            <div className="bg-red-100 dark:bg-red-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-red-200 dark:border-red-800/60 cursor-default select-none">
                                <h4 className={`${ui.text.sm} lg:text-base font-bold text-red-700 dark:text-red-300 uppercase tracking-wider`}>
                                    Deleted ({deletedCount})
                                </h4>
                            </div>
                            <div className="px-2 lg:px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/40 cursor-default select-none">
                                <span className={`${ui.text.xs} font-bold text-red-800 dark:text-red-200 uppercase tracking-wide`}>
                                    Domain {'>'} Entity Name {'>'} Action
                                </span>
                            </div>
                            <div
                                className={`${ui.widthAndHeight.comparisonColumnHeight} overflow-y-auto bg-red-50/30 dark:bg-red-950/10 cursor-default`}
                            >
                                {node.children
                                    .filter((c) => c.changeType === 'Deleted')
                                    .map((child, idx) => (
                                        <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Case 4 & 5: depth 0, all other properties with children ──────────────
    if (depth === 0 && hasChildren && node.children) {
        const hasContainerChildren = node.children.some(
            (child) => child.children && child.children.length > 0
        );

        if (hasContainerChildren) {
            const borderColor =
                node.changeType === 'Added'
                    ? 'border-green-300 dark:border-green-700'
                    : node.changeType === 'Deleted'
                        ? 'border-red-300 dark:border-red-700'
                        : 'border-orange-300 dark:border-orange-700';
            const textColor =
                node.changeType === 'Added'
                    ? 'text-green-700 dark:text-green-300'
                    : node.changeType === 'Deleted'
                        ? 'text-red-700 dark:text-red-300'
                        : 'text-orange-700 dark:text-orange-300';

            return (
                <div className="pl-8 lg:pl-10 pr-4 py-3 lg:py-4 cursor-default">
                    <div className={`border-l-4 ${borderColor} pl-4 mb-4 cursor-default select-none`}>
                        <div className="flex items-center gap-3 flex-wrap">
                            <span className={`${ui.text.sectionTitle} font-bold ${textColor} select-text`}>
                                {formatPropertyName(node.propertyName)}
                            </span>
                            <StatusBadge status={node.changeType} />
                            <span
                                className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full ${node.changeType === 'Added'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                    : node.changeType === 'Deleted'
                                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                        : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                                    }`}
                            >
                                {node.children.length} {node.children.length === 1 ? 'section' : 'sections'}
                            </span>
                        </div>
                    </div>
                    <div className="space-y-4 lg:space-y-6 cursor-default">
                        {node.children.map((child, idx) => (
                            <div key={`${child.jsonPath}-${idx}`} className="cursor-default">
                                <PropertyChangeNode node={child} depth={1} index={idx} />
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // Leaf children → table layout
        const addedCount = node.children.filter((c) => getActualChangeType(c) === 'Added').length;
        const deletedCount = node.children.filter((c) => getActualChangeType(c) === 'Deleted').length;
        const modifiedCount = node.children.filter((c) => getActualChangeType(c) === 'Modified').length;
        const totalChanges = addedCount + deletedCount + modifiedCount;

        const borderColor =
            node.changeType === 'Added'
                ? 'border-green-300 dark:border-green-700'
                : node.changeType === 'Deleted'
                    ? 'border-red-300 dark:border-red-700'
                    : 'border-orange-300 dark:border-orange-700';
        const textColor =
            node.changeType === 'Added'
                ? 'text-green-700 dark:text-green-300'
                : node.changeType === 'Deleted'
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-orange-700 dark:text-orange-300';

        const modifiedTable = modifiedCount > 0 && (
            <div key="modified" className="border-2 border-orange-200 dark:border-orange-800/60 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-orange-100 dark:bg-orange-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-orange-200 dark:border-orange-800/60 cursor-default select-none">
                    <h4 className={`${ui.text.sm} font-bold text-orange-700 dark:text-orange-300 uppercase tracking-wider`}>
                        Modified ({modifiedCount})
                    </h4>
                </div>
                <div className="grid grid-cols-[1fr_1.5fr_1.5fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800/40 cursor-default select-none">
                    <span className={`${ui.text.xs} font-bold text-orange-800 dark:text-orange-200 uppercase tracking-wide`}>Property</span>
                    <span className={`${ui.text.xs} font-bold text-orange-800 dark:text-orange-200 uppercase tracking-wide`}>Before</span>
                    <span className={`${ui.text.xs} font-bold text-orange-800 dark:text-orange-200 uppercase tracking-wide`}>After</span>
                </div>
                <div className="bg-orange-50/30 dark:bg-orange-950/10">
                    {node.children
                        .filter((c) => getActualChangeType(c) === 'Modified')
                        .map((child, idx) => (
                            <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                        ))}
                </div>
            </div>
        );

        const addedTable = addedCount > 0 && (
            <div key="added" className="border-2 border-green-200 dark:border-green-800/60 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-green-100 dark:bg-green-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-green-200 dark:border-green-800/60 cursor-default select-none">
                    <h4 className={`${ui.text.sm} lg:text-base font-bold text-green-700 dark:text-green-300 uppercase tracking-wider`}>
                        Added ({addedCount})
                    </h4>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800/40 cursor-default select-none">
                    <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>Property</span>
                    <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>Value</span>
                </div>
                <div className="bg-green-50/30 dark:bg-green-950/10">
                    {node.children
                        .filter((c) => getActualChangeType(c) === 'Added')
                        .map((child, idx) => (
                            <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                        ))}
                </div>
            </div>
        );

        const deletedTable = deletedCount > 0 && (
            <div key="deleted" className="border-2 border-red-200 dark:border-red-800/60 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-red-100 dark:bg-red-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-red-200 dark:border-red-800/60 cursor-default select-none">
                    <h4 className={`${ui.text.sm} font-bold text-red-700 dark:text-red-300 uppercase tracking-wider`}>
                        Deleted ({deletedCount})
                    </h4>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/40 cursor-default select-none">
                    <span className={`${ui.text.xs} font-bold text-red-800 dark:text-red-200 uppercase tracking-wide`}>Property</span>
                    <span className={`${ui.text.xs} font-bold text-red-800 dark:text-red-200 uppercase tracking-wide`}>Value</span>
                </div>
                <div className="bg-red-50/30 dark:bg-red-950/10">
                    {node.children
                        .filter((c) => getActualChangeType(c) === 'Deleted')
                        .map((child, idx) => (
                            <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                        ))}
                </div>
            </div>
        );

        const tables = [modifiedTable, addedTable, deletedTable].filter(Boolean);
        const tableCount = tables.length;

        return (
            <div className="pl-8 lg:pl-10 pr-4 py-3 lg:py-4 cursor-default">
                <div className={`border-l-4 ${borderColor} pl-4 mb-4 cursor-default select-none`}>
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className={`${ui.text.sectionTitle} font-bold ${textColor} select-text`}>
                            {formatPropertyName(node.propertyName)}
                        </span>
                        <StatusBadge status={node.changeType} />
                        <span
                            className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full ${node.changeType === 'Added'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : node.changeType === 'Deleted'
                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                    : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                                }`}
                        >
                            {totalChanges} {totalChanges === 1 ? 'change' : 'changes'}
                        </span>
                    </div>
                </div>
                <div className="cursor-default">
                    {tableCount === 1 && <div>{tables[0]}</div>}
                    {tableCount === 2 && (
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
                            {tables[0]}
                            {tables[1]}
                        </div>
                    )}
                    {tableCount === 3 && (
                        <>
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6 mb-4 lg:mb-6">
                                {tables[0]}
                                {tables[1]}
                            </div>
                            <div>{tables[2]}</div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ── Case 6: depth 1, branch with children — sub-section tables ────────────
    if (depth === 1 && hasChildren && node.children) {
        const getAllLeafNodes = (nodes: ComparisonPropertyChange[]): ComparisonPropertyChange[] => {
            const leaves: ComparisonPropertyChange[] = [];
            for (const n of nodes) {
                if (n.children && n.children.length > 0) {
                    leaves.push(...getAllLeafNodes(n.children));
                } else {
                    leaves.push(n);
                }
            }
            return leaves;
        };

        const leafChildren = getAllLeafNodes(node.children);
        const addedCount = leafChildren.filter((c) => getActualChangeType(c) === 'Added').length;
        const deletedCount = leafChildren.filter((c) => getActualChangeType(c) === 'Deleted').length;
        const modifiedCount = leafChildren.filter((c) => getActualChangeType(c) === 'Modified').length;

        const modifiedTable = modifiedCount > 0 && (
            <div key="modified" className="border-2 border-orange-200 dark:border-orange-800/60 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-orange-100 dark:bg-orange-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-orange-200 dark:border-orange-800/60 cursor-default select-none">
                    <h4 className={`${ui.text.sm} font-bold text-orange-700 dark:text-orange-300 uppercase tracking-wider`}>
                        Modified ({modifiedCount})
                    </h4>
                </div>
                <div className="grid grid-cols-[1fr_1.5fr_1.5fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800/40 cursor-default select-none">
                    <span className={`${ui.text.xs} font-bold text-orange-800 dark:text-orange-200 uppercase tracking-wide`}>Property</span>
                    <span className={`${ui.text.xs} font-bold text-orange-800 dark:text-orange-200 uppercase tracking-wide`}>Before</span>
                    <span className={`${ui.text.xs} font-bold text-orange-800 dark:text-orange-200 uppercase tracking-wide`}>After</span>
                </div>
                <div className="bg-orange-50/30 dark:bg-orange-950/10">
                    {leafChildren
                        .filter((c) => getActualChangeType(c) === 'Modified')
                        .map((child, idx) => (
                            <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                        ))}
                </div>
            </div>
        );

        const addedTable = addedCount > 0 && (
            <div key="added" className="border-2 border-green-200 dark:border-green-800/60 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-green-100 dark:bg-green-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-green-200 dark:border-green-800/60 cursor-default select-none">
                    <h4 className={`${ui.text.sm} font-bold text-green-700 dark:text-green-300 uppercase tracking-wider`}>
                        Added ({addedCount})
                    </h4>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800/40 cursor-default select-none">
                    <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>Property</span>
                    <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>Value</span>
                </div>
                <div className="bg-green-50/30 dark:bg-green-950/10">
                    {leafChildren
                        .filter((c) => getActualChangeType(c) === 'Added')
                        .map((child, idx) => (
                            <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                        ))}
                </div>
            </div>
        );

        const deletedTable = deletedCount > 0 && (
            <div key="deleted" className="border-2 border-red-200 dark:border-red-800/60 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-red-100 dark:bg-red-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-red-200 dark:border-red-800/60 cursor-default select-none">
                    <h4 className={`${ui.text.sm} font-bold text-red-700 dark:text-red-300 uppercase tracking-wider`}>
                        Deleted ({deletedCount})
                    </h4>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/40 cursor-default select-none">
                    <span className={`${ui.text.xs} font-bold text-red-800 dark:text-red-200 uppercase tracking-wide`}>Property</span>
                    <span className={`${ui.text.xs} font-bold text-red-800 dark:text-red-200 uppercase tracking-wide`}>Value</span>
                </div>
                <div className="bg-red-50/30 dark:bg-red-950/10">
                    {leafChildren
                        .filter((c) => getActualChangeType(c) === 'Deleted')
                        .map((child, idx) => (
                            <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                        ))}
                </div>
            </div>
        );

        const tables = [modifiedTable, addedTable, deletedTable].filter(Boolean);
        const tableCount = tables.length;

        return (
            <div className="pl-4 lg:pl-6 cursor-default">
                <h3 className={`${ui.text.body} font-bold text-gray-700 dark:text-gray-300 mb-3 cursor-default select-text`}>
                    {formatPropertyName(node.propertyName)}
                </h3>
                {tableCount === 1 && <div>{tables[0]}</div>}
                {tableCount === 2 && (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
                        {tables[0]}
                        {tables[1]}
                    </div>
                )}
                {tableCount === 3 && (
                    <>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6 mb-4 lg:mb-6">
                            {tables[0]}
                            {tables[1]}
                        </div>
                        <div>{tables[2]}</div>
                    </>
                )}
            </div>
        );
    }

    // ── Case 7: other branch nodes — flatten ──────────────────────────────────
    if (node.children && node.children.length > 0) {
        return (
            <>
                {node.children.map((child, childIdx) => (
                    <PropertyChangeNode
                        key={`${child.jsonPath}-${childIdx}`}
                        node={child}
                        depth={depth}
                        index={childIdx}
                    />
                ))}
            </>
        );
    }

    return null;
};

// ── StatusBadge ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Partial<Record<ChangeStatus, { label: string; classes: string }>> = {
    Added: {
        label: 'Added',
        classes: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    },
    Modified: {
        label: 'Modified',
        classes: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    },
    Deleted: {
        label: 'Removed',
        classes: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    },
};

export const StatusBadge: React.FC<{ status: ChangeStatus }> = ({ status }) => {
    const config = STATUS_CONFIG[status];
    if (!config) return null;
    return (
        <span
            className={`${ui.text.xs} font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${config.classes}`}
        >
            {config.label}
        </span>
    );
};

// ── ResourceTypeRow ────────────────────────────────────────────────────────

export const ResourceTypeRow: React.FC<{ group: SnapshotResourceTypeGroup }> = ({ group }) => {
    const { added, modified, deleted } = group.summary;
    const total = added + modified + deleted;
    const friendlyName = getTypeLabelByApiName(toFriendlyName(group.resourceType));
    const changedInstances = group.instances.filter((i) => i.status !== 'Unchanged');

    return (
        <details className="group border-b border-gray-100 dark:border-gray-800 last:border-0">
            <summary
                className={`flex items-center justify-between ${ui.page.rowPadding} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors list-none select-none`}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <svg
                        className={`${ui.svg.sm} shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-90`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                        />
                    </svg>
                    <span
                        className={`${ui.text.xs} font-bold text-white bg-blue-600 dark:bg-blue-700 px-2 py-0.5 rounded-full shrink-0`}
                    >
                        {total}
                    </span>
                    <span
                        className={`${ui.text.body} font-semibold text-gray-800 dark:text-gray-100 truncate`}
                        title={friendlyName}
                    >
                        {friendlyName}
                    </span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 ml-3">
                    {added > 0 && (
                        <span
                            className={`${ui.text.xs} font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300`}
                        >
                            +{added}
                        </span>
                    )}
                    {modified > 0 && (
                        <span
                            className={`${ui.text.xs} font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300`}
                        >
                            ~{modified}
                        </span>
                    )}
                    {deleted > 0 && (
                        <span
                            className={`${ui.text.xs} font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`}
                        >
                            -{deleted}
                        </span>
                    )}
                </div>
            </summary>

            <div className="bg-gray-50 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-800">
                {changedInstances.map((item) => {
                    const hasDetails =
                        item.properties.length > 0 ||
                        (item.status === 'Added' && item.afterState) ||
                        (item.status === 'Deleted' && item.beforeState);

                    const displayText =
                        group.resourceType === 'genesyscloud_location'
                            ? formatPropertyName(item.label)
                            : (item.displayName || item.label || '')
                                .replace(/_/g, ' ')
                                .replace(/([a-z])([A-Z])/g, '$1 $2');

                    return (
                        <details
                            key={item.label}
                            className={`group/item border-b border-gray-100 dark:border-gray-800/60 last:border-0 ${hasDetails ? 'cursor-pointer' : ''
                                }`}
                        >
                            <summary
                                className={`flex items-center justify-between pl-8 pr-4 py-2.5 transition-colors list-none ${item.status === 'Added'
                                    ? 'bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30'
                                    : item.status === 'Modified'
                                        ? 'bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/20 dark:hover:bg-orange-900/30'
                                        : item.status === 'Deleted'
                                            ? 'bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30'
                                            : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                                    } ${hasDetails ? 'cursor-pointer' : 'cursor-default'}`}
                            >
                                <div className="flex items-center gap-2.5 min-w-0">
                                    {hasDetails ? (
                                        <svg
                                            className={`${ui.svg.sm} shrink-0 transition-transform duration-200 group-open/item:rotate-90 ${item.status === 'Added'
                                                ? 'text-green-400 dark:text-green-500'
                                                : item.status === 'Modified'
                                                    ? 'text-orange-400 dark:text-orange-500'
                                                    : item.status === 'Deleted'
                                                        ? 'text-red-400 dark:text-red-500'
                                                        : 'text-gray-300 dark:text-gray-600'
                                                }`}
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    ) : (
                                        <span className="w-5 shrink-0" />
                                    )}
                                    <span
                                        className={`${ui.text.label} truncate font-medium select-text ${item.status === 'Added'
                                            ? 'text-green-700 dark:text-green-300'
                                            : item.status === 'Modified'
                                                ? 'text-orange-700 dark:text-orange-300'
                                                : item.status === 'Deleted'
                                                    ? 'text-red-700 dark:text-red-300'
                                                    : 'text-gray-700 dark:text-gray-300'
                                            }`}
                                        title={displayText}
                                    >
                                        {displayText}
                                    </span>
                                </div>
                                <StatusBadge status={item.status} />
                            </summary>

                            {hasDetails
                                ? (() => {
                                    // Modified / Deleted items with property changes
                                    if (item.properties.length > 0) {
                                        return (
                                            <div className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800/60">
                                                {item.properties.map((prop, idx) => (
                                                    <PropertyChangeNode
                                                        key={`${prop.jsonPath}-${idx}`}
                                                        node={prop}
                                                        depth={0}
                                                        index={idx}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    }

                                    // Added items with afterState
                                    if (item.status === 'Added' && item.afterState) {
                                        const state = item.afterState as Record<string, unknown>;
                                        const hasPermissionPolicies =
                                            Array.isArray(state['permission_policies']) &&
                                            (state['permission_policies'] as unknown[]).length > 0;

                                        if (hasPermissionPolicies) {
                                            const policies = state['permission_policies'] as Array<Record<string, unknown>>;
                                            return (
                                                <div className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800/60">
                                                    <div className="pl-8 lg:pl-10 pr-4 py-3 lg:py-4 cursor-default">
                                                        <div className="border-l-4 border-green-300 dark:border-green-700 pl-4 mb-4 cursor-default select-none">
                                                            <div className="flex items-center gap-3 flex-wrap">
                                                                <span className={`${ui.text.sectionTitle} font-bold text-green-700 dark:text-green-300 select-text`}>
                                                                    Permission Policies
                                                                </span>
                                                                <StatusBadge status="Added" />
                                                                <span className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300`}>
                                                                    {policies.length} {policies.length === 1 ? 'policy' : 'policies'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="border-2 border-green-200 dark:border-green-800/60 rounded-lg overflow-hidden shadow-sm">
                                                            <div className="bg-green-100 dark:bg-green-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-green-200 dark:border-green-800/60 cursor-default select-none">
                                                                <h4 className={`${ui.text.sm} lg:text-base font-bold text-green-700 dark:text-green-300 uppercase tracking-wider`}>
                                                                    Added ({policies.length})
                                                                </h4>
                                                            </div>
                                                            <div className="px-2 lg:px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800/40 cursor-default select-none">
                                                                <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>
                                                                    Domain {'>'} Entity Name {'>'} Action
                                                                </span>
                                                            </div>
                                                            <div className="max-h-96 overflow-y-auto bg-green-50/30 dark:bg-green-950/10 cursor-default">
                                                                {policies.map((policy, idx) => {
                                                                    const domain = (policy['domain'] as string) || '';
                                                                    const entityName =
                                                                        (policy['entity_name'] as string) ||
                                                                        (policy['entityName'] as string) ||
                                                                        '';
                                                                    const actionSet = policy['action_set'] as string[] | undefined;
                                                                    const hasAllPerms = actionSet?.includes('*');
                                                                    const displayDomain = camelCaseToTitleCase(domain);
                                                                    const displayEntity = camelCaseToTitleCase(entityName);
                                                                    const displayAction = hasAllPerms
                                                                        ? 'All Permissions'
                                                                        : actionSet?.join(', ') || '—';
                                                                    const breadcrumb = `${displayDomain} > ${displayEntity} > ${displayAction}`;
                                                                    const isEvenRow = idx % 2 === 0;
                                                                    const rowBg = isEvenRow
                                                                        ? 'bg-green-50/40 dark:bg-green-950/15'
                                                                        : 'bg-green-100/80 dark:bg-green-900/25';
                                                                    return (
                                                                        <div
                                                                            key={idx}
                                                                            className={`px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 hover:bg-green-200 dark:hover:bg-green-900/35 transition-colors cursor-default ${rowBg}`}
                                                                        >
                                                                            <span
                                                                                className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 select-text`}
                                                                                title={breadcrumb}
                                                                            >
                                                                                {breadcrumb}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        // Other Added items — general state properties table
                                        const stateEntries = Object.entries(state).filter(
                                            ([, value]) => value !== null && value !== undefined
                                        );
                                        if (stateEntries.length > 0) {
                                            return (
                                                <div className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800/60">
                                                    <div className="pl-8 lg:pl-10 pr-4 py-3 lg:py-4 cursor-default">
                                                        <div className="border-l-4 border-green-300 dark:border-green-700 pl-4 mb-4 cursor-default select-none">
                                                            <div className="flex items-center gap-3 flex-wrap">
                                                                <span className={`${ui.text.sectionTitle} font-bold text-green-700 dark:text-green-300 select-text`}>
                                                                    Properties
                                                                </span>
                                                                <StatusBadge status="Added" />
                                                                <span className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300`}>
                                                                    {stateEntries.length}{' '}
                                                                    {stateEntries.length === 1 ? 'property' : 'properties'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="border-2 border-green-200 dark:border-green-800/60 rounded-lg overflow-hidden shadow-sm">
                                                            <div className="bg-green-100 dark:bg-green-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-green-200 dark:border-green-800/60 cursor-default select-none">
                                                                <h4 className={`${ui.text.sm} lg:text-base font-bold text-green-700 dark:text-green-300 uppercase tracking-wider`}>
                                                                    Added ({stateEntries.length})
                                                                </h4>
                                                            </div>
                                                            <div className="grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800/40 cursor-default select-none">
                                                                <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>
                                                                    Property
                                                                </span>
                                                                <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>
                                                                    Value
                                                                </span>
                                                            </div>
                                                            <div className="max-h-96 overflow-y-auto bg-green-50/30 dark:bg-green-950/10 cursor-default">
                                                                {stateEntries.map(([key, value], idx) => {
                                                                    const formattedKey = formatPropertyName(key);
                                                                    const isEvenRow = idx % 2 === 0;
                                                                    const rowBg = isEvenRow
                                                                        ? 'bg-green-50/40 dark:bg-green-950/15'
                                                                        : 'bg-green-100/80 dark:bg-green-900/25';

                                                                    if (
                                                                        (key === 'member_ids' || key === 'column_names') &&
                                                                        Array.isArray(value) &&
                                                                        (value as unknown[]).length > 0
                                                                    ) {
                                                                        return (
                                                                            <div
                                                                                key={idx}
                                                                                className={`grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 hover:bg-green-200 dark:hover:bg-green-900/35 transition-colors cursor-default ${rowBg}`}
                                                                            >
                                                                                <span
                                                                                    className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 font-medium select-text`}
                                                                                    title={formattedKey}
                                                                                >
                                                                                    {formattedKey}
                                                                                </span>
                                                                                <div className="border border-green-200 dark:border-green-700 rounded overflow-hidden">
                                                                                    <div className="max-h-32 overflow-y-auto bg-white dark:bg-gray-800">
                                                                                        {(value as unknown[]).map((item, itemIdx) => {
                                                                                            const displayItem = extractResourceName(String(item));
                                                                                            const itemBg =
                                                                                                itemIdx % 2 === 0
                                                                                                    ? 'bg-gray-50 dark:bg-gray-900'
                                                                                                    : 'bg-white dark:bg-gray-800';
                                                                                            return (
                                                                                                <div
                                                                                                    key={itemIdx}
                                                                                                    className={`px-2 py-1 border-b border-green-100 dark:border-green-800/40 last:border-0 ${itemBg}`}
                                                                                                >
                                                                                                    <span
                                                                                                        className={`${ui.text.xs} text-gray-800 dark:text-gray-100 select-text`}
                                                                                                        title={displayItem}
                                                                                                    >
                                                                                                        {displayItem}
                                                                                                    </span>
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }

                                                                    let displayValue: string;
                                                                    if (Array.isArray(value)) {
                                                                        displayValue = (value as unknown[])
                                                                            .map((v) => extractResourceName(String(v)))
                                                                            .join(', ');
                                                                    } else if (typeof value === 'boolean') {
                                                                        displayValue = value ? 'true' : 'false';
                                                                    } else if (typeof value === 'object' && value !== null) {
                                                                        // Flatten nested object to readable "Key: value" pairs
                                                                        const pairs = Object.entries(value as Record<string, unknown>)
                                                                            .filter(([, v]) => v !== null && v !== undefined && String(v) !== '[object Object]')
                                                                            .map(([k, v]) => `${formatPropertyName(k)}: ${Array.isArray(v) ? (v as unknown[]).join(', ') : String(v)}`);
                                                                        displayValue = pairs.length > 0 ? pairs.join(' · ') : '—';
                                                                    } else {
                                                                        const raw = String(value);
                                                                        displayValue = raw === '[object Object]' ? '—' : extractResourceName(raw);
                                                                    }

                                                                    return (
                                                                        <div
                                                                            key={idx}
                                                                            className={`grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 hover:bg-green-200 dark:hover:bg-green-900/35 transition-colors cursor-default items-center ${rowBg}`}
                                                                        >
                                                                            <span
                                                                                className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 font-medium truncate select-text`}
                                                                                title={formattedKey}
                                                                            >
                                                                                {formattedKey}
                                                                            </span>
                                                                            <span
                                                                                className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 truncate select-text`}
                                                                                title={displayValue}
                                                                            >
                                                                                {displayValue}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        // Fallback for Added items with no displayable state
                                        return (
                                            <div className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800/60 p-4">
                                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                                    Resource added (no property details available)
                                                </div>
                                            </div>
                                        );
                                    }

                                    return null;
                                })()
                                : null}
                        </details>
                    );
                })}
            </div>
        </details>
    );
};

// ── SnapshotComparisonView (top-level) ─────────────────────────────────────

interface SnapshotComparisonViewProps {
    resourceTypes: SnapshotResourceTypeGroup[];
}

const SnapshotComparisonView: React.FC<SnapshotComparisonViewProps> = ({ resourceTypes }) => {
    const changedGroups = resourceTypes.filter(
        (g) => g.summary.added + g.summary.modified + g.summary.deleted > 0
    );

    if (changedGroups.length === 0) {
        return (
            <div className={`py-10 text-center ${ui.text.body} text-gray-400 dark:text-gray-500 italic`}>
                No changes detected for this migration.
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
            {changedGroups.map((group) => (
                <ResourceTypeRow key={group.resourceType} group={group} />
            ))}
        </div>
    );
};

export default SnapshotComparisonView;
